import { Canvas } from "@react-three/fiber"
import { Center, Gltf, OrbitControls, BakeShadows, Sky, Environment } from "@react-three/drei"
import { EffectComposer, N8AO, TiltShift2, Noise } from "@react-three/postprocessing"
import { Suspense, useEffect, useRef } from "react"
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib"
import { getCdnUrl } from "@/config/cdn"

/*
Model author: Igor Tretyakov (https://sketchfab.com/vdv77)
License: CC-BY-4.0 (http://creativecommons.org/licenses/by/4.0/)
Source: https://sketchfab.com/3d-models/low-poly-city-pack-7f671f35e9ad4149b83451a3a92a6e2e
Title: low poly city pack
*/

function CityModel() {
  return (
    <Center position={[0, 2, 0]}>
      <Gltf castShadow receiveShadow scale={0.1} src={getCdnUrl("models/city-gt.glb")} />
    </Center>
  )
}

function LoadingFallback() {
  return null
}

function DisabledZoomControls() {
  const controlsRef = useRef<OrbitControlsImpl>(null)
  
  useEffect(() => {
    if (controlsRef.current) {
      controlsRef.current.enableZoom = false
    }
  }, [])
  
  return (
    <OrbitControls
      ref={controlsRef}
      zoomSpeed={0.75}
      minZoom={20}
      maxZoom={200}
      autoRotate
      autoRotateSpeed={-1.0}
      enablePan={false}
    />
  )
}

interface R3FSceneProps {
  className?: string
  style?: React.CSSProperties
}

export default function R3FScene({ className = '', style }: R3FSceneProps) {
  return (
    <div className={`w-full h-screen relative ${className}`} style={{ ...style, background: 'linear-gradient(to bottom, #FF6B35 0%, #F7931E 40%, #FDB777 70%, #FFD3A5 100%)' }}>
      <div className="w-1/2 mx-auto h-full">
        <Canvas orthographic shadows gl={{ antialias: false, alpha: false }} camera={{ position: [100, 50, -100], zoom: 55, near: 0.1, far: 2000 }}>
          <Suspense fallback={<LoadingFallback />}>
            <Environment preset="sunset" />
          </Suspense>
          <color attach="background" args={['#FF8C42']} />
          <fog attach="fog" args={['#FFB88C', 100, 500]} />
          <hemisphereLight intensity={0.4} color="#FFB88C" groundColor="#FF6B35" />
          <directionalLight color="#FF6B35" intensity={3.0} position={[50, 10, 30]} castShadow shadow-mapSize={512} shadow-bias={-0.0004}>
            <orthographicCamera attach="shadow-camera" args={[-40, 40,   40, -40, 1, 1000]} />
          </directionalLight>
          <Suspense fallback={<LoadingFallback />}>
            <CityModel />
          </Suspense>
          <EffectComposer enableNormalPass={false} multisampling={4}>
            <N8AO aoRadius={50} distanceFalloff={0.2} intensity={6} screenSpaceRadius halfRes />
            <TiltShift2 />
            <Noise opacity={0.15} />
          </EffectComposer>
          <DisabledZoomControls />
          <BakeShadows />

        </Canvas>
      </div>
      {/* Porthole overlay - frames the 3D scene */}
      <img
        src={getCdnUrl("images/porthole.png")}
        alt=""
        className="absolute inset-0 w-full h-full object-cover pointer-events-none z-10"
        style={{ mixBlendMode: 'normal' }}
      />
    </div>
  )
}
